import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { isEditableElement } from "../../hooks/UseShortcutsHook";
import {
  SequenceLocator,
  SequenceSelectionIds,
} from "../../app/slices/helpers/SequenceMove";
import type { QuizFeedbackSlot } from "../../models/Quiz";

export type CopyPasteContextType = "timeline" | "sequence";

export type PendingCutState =
  | {
      kind: "timelineInteraction";
      cutId: string;
      interactionId: string;
    }
  | {
      kind: "timelineInteractions";
      cutId: string;
      interactionIds: string[];
    }
  | {
      kind: "sequenceSelection";
      cutId: string;
      source: SequenceLocator;
      selection: SequenceSelectionIds;
    };

export interface CopyPasteContextValue {
  pasteContext: CopyPasteContextType;
  setPasteContext: (context: CopyPasteContextType) => void;
  pendingCut?: PendingCutState | null;
  beginPendingCut?: (pendingCut: PendingCutState) => void;
  clearPendingCut?: () => void;
}

export const CopyPasteContext = createContext<CopyPasteContextValue | null>(
  null
);

// The timeline clipboard handlers (from useTimelineCopyPaste), provided once
// where the keyboard shortcuts bind so row menus can reuse them without each
// row instantiating the heavy hook.
export interface TimelineClipboardActions {
  handleCopy: () => void;
  handleCut: () => void;
  handlePaste: () => void | Promise<void>;
  // Pastes directly into one quiz feedback slot (append) — the explicit
  // target used by a slot's context menu, so empty slots are pasteable.
  handlePasteIntoSlot: (
    quizId: string,
    slot: QuizFeedbackSlot
  ) => void | Promise<void>;
}

export const TimelineClipboardContext =
  createContext<TimelineClipboardActions | null>(null);

export function CopyPasteProvider({ children }: { children: React.ReactNode }) {
  const [pasteContext, setPasteContextState] =
    useState<CopyPasteContextType>("timeline");
  const [pendingCut, setPendingCut] = useState<PendingCutState | null>(null);
  const setPasteContext = useCallback((context: CopyPasteContextType) => {
    setPasteContextState(context);
  }, []);
  const beginPendingCut = useCallback((nextPendingCut: PendingCutState) => {
    setPendingCut(nextPendingCut);
  }, []);
  const clearPendingCut = useCallback(() => {
    setPendingCut(null);
  }, []);

  useEffect(() => {
    if (!pendingCut) return;

    const cancelPendingCut = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.isComposing ||
        event.key !== "Escape" ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        isEditableElement(event.target)
      ) {
        return;
      }

      event.preventDefault();
      clearPendingCut();
    };

    document.addEventListener("keydown", cancelPendingCut);
    return () => document.removeEventListener("keydown", cancelPendingCut);
  }, [clearPendingCut, pendingCut]);

  const value = useMemo<CopyPasteContextValue>(
    () => ({
      pasteContext,
      setPasteContext,
      pendingCut,
      beginPendingCut,
      clearPendingCut,
    }),
    [
      beginPendingCut,
      clearPendingCut,
      pasteContext,
      pendingCut,
      setPasteContext,
    ]
  );

  return (
    <CopyPasteContext.Provider value={value}>
      {children}
    </CopyPasteContext.Provider>
  );
}
